import { Button, Divider, Stack, Typography } from '@mui/material';
import { TrashSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useApiCallable } from '../api';
import ConfirmModal from './ConfirmModal';

interface ContentItem {
  id: string;
  title?: string;
  publishedAt?: string | null;
}

interface PublishingHandlers {
  handleSaveDraft: () => void;
  handlePublish: () => void;
}

interface PublishingSectionProps {
  existing?: Partial<ContentItem> | null;
  isLoading: boolean;
  contentType: string;
  apiPath: string;
  publishing: PublishingHandlers;
}

export default function PublishingSection({
  existing,
  isLoading,
  contentType,
  apiPath,
  publishing,
}: PublishingSectionProps) {
  const navigate = useNavigate();
  const callApi = useApiCallable();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmUnpublishOpen, setConfirmUnpublishOpen] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);

  const isExisting = !!existing;
  const isPublished = !!existing?.publishedAt;
  const contentId = existing?.id;
  const contentTitle = existing?.title;

  const saveButtonText = !isExisting
    ? 'Save as Draft'
    : isPublished
      ? 'Save & Publish Changes'
      : 'Save Changes';

  const handlePublishConfirm = () => {
    setConfirmPublishOpen(false);
    publishing.handlePublish();
  };

  const handleUnpublishConfirm = async ({
    setLoading,
  }: {
    setLoading: (loading: boolean) => void;
  }) => {
    if (!contentId) return;

    setLoading(true);
    const response = await callApi(
      `/${apiPath}/${contentId}/mark-as-unpublished`,
      {
        method: 'POST',
        jsonBody: {},
      },
    );

    if (response) {
      navigate(0);
      toast.success(`${contentType} unpublished successfully`);
    }
    setLoading(false);
  };

  const handleDeleteConfirm = async ({
    setLoading,
  }: {
    setLoading: (loading: boolean) => void;
  }) => {
    if (!contentId) return;

    setLoading(true);
    const response = await callApi(`/${apiPath}/${contentId}`, {
      method: 'DELETE',
    });

    if (response) {
      navigate(-1);
      toast.success(`${contentType} deleted successfully`);
    }
    setLoading(false);
  };

  return (
    <Stack gap={3}>
      <Divider />
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" gap={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={publishing.handleSaveDraft}
            size="large"
            disabled={isLoading}
          >
            {saveButtonText}
          </Button>
          {!isPublished ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => setConfirmPublishOpen(true)}
              size="large"
              disabled={isLoading}
            >
              Publish Now
            </Button>
          ) : (
            <Button
              variant="contained"
              color="warning"
              onClick={() => setConfirmUnpublishOpen(true)}
              size="large"
            >
              Unpublish
            </Button>
          )}
        </Stack>
        {isExisting && (
          <Button
            variant="contained"
            color="error"
            onClick={() => setConfirmDeleteOpen(true)}
            size="large"
            startIcon={<TrashSimple />}
          >
            Delete
          </Button>
        )}
      </Stack>

      {confirmPublishOpen && (
        <ConfirmModal
          title={`Publish ${contentType}`}
          onClose={() => setConfirmPublishOpen(false)}
          onConfirm={handlePublishConfirm}
          confirmText="Publish"
          color="success"
        >
          <Typography>
            Are you sure you want to publish this content? This will make it
            visible to everyone on the app.
          </Typography>
        </ConfirmModal>
      )}

      {confirmDeleteOpen && (
        <ConfirmModal
          title={`Delete ${contentType}`}
          onClose={() => setConfirmDeleteOpen(false)}
          onConfirm={handleDeleteConfirm}
        >
          <Typography>
            Are you sure you want to delete
            {contentTitle
              ? ` '${contentTitle}'`
              : ` this ${contentType.toLowerCase()}`}
            ?
          </Typography>
        </ConfirmModal>
      )}

      {confirmUnpublishOpen && (
        <ConfirmModal
          title={`Unpublish ${contentType}`}
          color="warning"
          onClose={() => setConfirmUnpublishOpen(false)}
          onConfirm={handleUnpublishConfirm}
        >
          <Typography>
            Are you sure you want to unpublish
            {contentTitle
              ? ` '${contentTitle}'`
              : ` this ${contentType.toLowerCase()}`}
            ? If it's already gone live, users will no longer be able to access
            it within the app.
          </Typography>
        </ConfirmModal>
      )}
    </Stack>
  );
}
